/**
 * TechniqueStatusStep Component
 *
 * Step for selecting the defense/detection status of a technique.
 * Used as step 2 in the Technique wizard.
 *
 * Features:
 * - Radio group for status selection with two-column layout
 * - Visual status indicators with colors
 * - Concise descriptions for each status option
 * - ARIA radiogroup pattern with roving tabindex
 * - Full keyboard navigation (arrow keys, Enter/Space)
 */

import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import type { KeyboardEvent } from 'react';
import { useCallback, useMemo, useRef } from 'react';

import type { TechniqueWizardFormData } from '@/sections/attacks/attackPaths/components/wizards/types';
import type { TechniqueStatus } from '@/sections/attacks/attackPaths/types';
import { cn } from '@/utils/classname';

/**
 * Status option configuration
 */
interface StatusOption {
  /** Status value */
  value: TechniqueStatus;
  /** Display label */
  label: string;
  /** Description of what this status means */
  description: string;
  /** Color variant for visual indicator */
  variant: 'neutral' | 'info' | 'warning' | 'critical' | 'success';
}

/**
 * All available status options for technique nodes.
 * Note: 'untested' is kept in the type for backward compatibility
 * but filtered out from UI (anything in an attack graph is tested by definition).
 */
const statusOptions: StatusOption[] = [
  {
    value: 'tested',
    label: 'Tested',
    description: 'Outcome is unknown',
    variant: 'info',
  },
  {
    value: 'undetected',
    label: 'Undetected',
    description: 'Not detected by defenses',
    variant: 'critical',
  },
  {
    value: 'detected',
    label: 'Detected',
    description: 'Detected by monitoring systems',
    variant: 'warning',
  },
  {
    value: 'logged',
    label: 'Logged',
    description: 'Logged but not alerted',
    variant: 'info',
  },
  {
    value: 'alerted',
    label: 'Alerted',
    description: 'Triggered security alert',
    variant: 'warning',
  },
  {
    value: 'prevented',
    label: 'Prevented',
    description: 'Blocked by defenses',
    variant: 'success',
  },
];

/**
 * Get CSS classes for status variant
 */
function getVariantClasses(
  variant: StatusOption['variant'],
  isSelected: boolean
): string {
  const baseClasses = isSelected
    ? 'border-2'
    : 'border hover:border-chariot-stroke-hover';

  switch (variant) {
    case 'success':
      return cn(
        baseClasses,
        isSelected
          ? 'border-green-500 bg-green-500/10'
          : 'border-chariot-stroke'
      );
    case 'warning':
      return cn(
        baseClasses,
        isSelected
          ? 'border-yellow-500 bg-yellow-500/10'
          : 'border-chariot-stroke'
      );
    case 'critical':
      return cn(
        baseClasses,
        isSelected ? 'border-red-500 bg-red-500/10' : 'border-chariot-stroke'
      );
    case 'info':
      return cn(
        baseClasses,
        isSelected ? 'border-blue-500 bg-blue-500/10' : 'border-chariot-stroke'
      );
    case 'neutral':
    default:
      return cn(
        baseClasses,
        isSelected
          ? 'border-chariot-brand-primary bg-chariot-brand-primary/10'
          : 'border-chariot-stroke'
      );
  }
}

/**
 * Props for TechniqueStatusStep component
 */
interface TechniqueStatusStepProps {
  /** Current wizard form data */
  formData: TechniqueWizardFormData;
  /** Form data setter */
  setFormData: (
    updater:
      | TechniqueWizardFormData
      | ((prev: TechniqueWizardFormData) => TechniqueWizardFormData)
  ) => void;
}

/** Number of columns in the grid layout */
const GRID_COLUMNS = 2;

/**
 * Status selection step for Technique wizard.
 *
 * Provides a radio group for selecting the defense/detection status
 * of the technique. Each status has a description and color indicator.
 * Uses a two-column grid layout to reduce modal height and implements
 * WAI-ARIA radio group pattern with roving tabindex for accessibility.
 *
 * @example
 * ```tsx
 * <TechniqueStatusStep
 *   formData={formData}
 *   setFormData={setFormData}
 * />
 * ```
 */
export function TechniqueStatusStep({
  formData,
  setFormData,
}: TechniqueStatusStepProps) {
  /** Refs for all option buttons for keyboard navigation */
  const optionsRef = useRef<(HTMLButtonElement | null)[]>([]);

  /**
   * Get the index of the currently selected option
   */
  const selectedIndex = useMemo(() => {
    const index = statusOptions.findIndex(opt => opt.value === formData.status);
    return index >= 0 ? index : 0;
  }, [formData.status]);

  /**
   * Handle status selection
   */
  const handleSelect = useCallback(
    (status: TechniqueStatus) => {
      setFormData(prev => ({
        ...prev,
        status,
      }));
    },
    [setFormData]
  );

  /**
   * Handle keyboard navigation following WAI-ARIA radio group pattern.
   * - ArrowLeft/ArrowRight: Move sequentially through options (DOM order)
   * - ArrowUp/ArrowDown: Move vertically within the same column
   * - Space/Enter: Select the focused option
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      let nextIndex: number | null = null;

      switch (event.key) {
        case 'ArrowRight':
          // Move to next option in DOM order (wraps around)
          nextIndex = (currentIndex + 1) % statusOptions.length;
          break;
        case 'ArrowLeft':
          // Move to previous option in DOM order (wraps around)
          nextIndex =
            (currentIndex - 1 + statusOptions.length) % statusOptions.length;
          break;
        case 'ArrowDown':
          // Move down in same column (add GRID_COLUMNS to index)
          nextIndex = currentIndex + GRID_COLUMNS;
          if (nextIndex >= statusOptions.length) {
            // Wrap to top of column
            nextIndex = currentIndex % GRID_COLUMNS;
          }
          break;
        case 'ArrowUp':
          // Move up in same column (subtract GRID_COLUMNS from index)
          nextIndex = currentIndex - GRID_COLUMNS;
          if (nextIndex < 0) {
            // Wrap to bottom of column
            const column = currentIndex % GRID_COLUMNS;
            const lastRowStart =
              Math.floor((statusOptions.length - 1) / GRID_COLUMNS) *
              GRID_COLUMNS;
            nextIndex = lastRowStart + column;
            // Handle case where last row doesn't have item in this column
            if (nextIndex >= statusOptions.length) {
              nextIndex = lastRowStart - GRID_COLUMNS + column;
            }
          }
          break;
        case ' ':
        case 'Enter':
          event.preventDefault();
          handleSelect(statusOptions[currentIndex].value);
          return;
        default:
          return;
      }

      if (nextIndex !== null) {
        event.preventDefault();
        // Focus and select the next option
        optionsRef.current[nextIndex]?.focus();
        handleSelect(statusOptions[nextIndex].value);
      }
    },
    [handleSelect]
  );

  return (
    <div className="flex flex-col gap-6" data-testid="technique-status-step">
      {/* Header */}
      <div>
        <h3
          id="status-selection-label"
          className="text-chariot-text-primary mb-2 text-lg font-semibold"
        >
          Select Defense Status
        </h3>
        <p
          id="status-selection-desc"
          className="text-chariot-text-secondary text-sm"
        >
          Choose the defense/detection status for this technique. This indicates
          how the technique performed against your security controls.
        </p>
      </div>

      {/* Status options - two-column grid layout */}
      <div
        role="radiogroup"
        aria-labelledby="status-selection-label"
        aria-describedby="status-selection-desc"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        data-testid="status-options"
      >
        {statusOptions.map((option, index) => {
          const isSelected = formData.status === option.value;

          return (
            <button
              key={option.value}
              ref={el => {
                optionsRef.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={index === selectedIndex ? 0 : -1}
              onClick={() => handleSelect(option.value)}
              onKeyDown={e => handleKeyDown(e, index)}
              className={cn(
                'flex items-start gap-3 rounded-lg p-3 text-left transition-colors',
                'focus:ring-chariot-brand-primary focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
                getVariantClasses(option.variant, isSelected)
              )}
              data-testid={`status-option-${option.value}`}
            >
              {/* Selection indicator */}
              <div className="mt-0.5 shrink-0">
                {isSelected ? (
                  <CheckCircleIcon className="text-chariot-brand-primary size-5" />
                ) : (
                  <div className="border-chariot-stroke size-5 rounded-full border-2" />
                )}
              </div>

              {/* Label and description */}
              <div className="flex-1">
                <div className="text-chariot-text-primary font-medium">
                  {option.label}
                </div>
                <div className="text-chariot-text-secondary mt-1 text-sm">
                  {option.description}
                </div>
              </div>

              {/* Status indicator icon */}
              <div className="mt-0.5 shrink-0">
                {option.variant === 'success' && (
                  <CheckCircleIcon className="size-5 text-green-500" />
                )}
                {option.variant === 'critical' && (
                  <XCircleIcon className="size-5 text-red-500" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selection summary */}
      <div aria-live="polite" className="text-sm">
        <span className="text-chariot-text-secondary">Selected status: </span>
        <span className="text-chariot-text-primary font-medium">
          {statusOptions.find(opt => opt.value === formData.status)?.label ||
            'Tested'}
        </span>
      </div>
    </div>
  );
}
